/**
 * AdminAffiliatedMinistries — the super admin desk for independent churches
 * and ministries under the Archbishop's covering.
 *
 * Applications · Active members · Lapsed · Fee configuration · Communications.
 * Nothing here touches the FWM church network.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  HandHeart, CheckCircle2, XCircle, Loader2, Send, Megaphone, Settings2,
  Inbox, Link2, RefreshCw, Wallet, MapPin, UserRound, Mail, Paperclip,
  Globe, EyeOff, Undo2, Award,
} from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatCurrency } from '@/lib/currency';
import { AffiliateAppearanceTab } from '@/components/affiliates/AffiliateAppearanceTab';

const STATUS_STYLE = {
  pending: 'bg-slate-100 text-slate-700',
  awaiting_payment: 'bg-amber-100 text-amber-800',
  active: 'bg-emerald-100 text-emerald-800',
  lapsed: 'bg-rose-100 text-rose-800',
  rejected: 'bg-slate-100 text-slate-500',
};
const STATUS_LABEL = {
  pending: 'Pending', awaiting_payment: 'Approved · fee unpaid',
  active: 'Approved', lapsed: 'Lapsed', rejected: 'Rejected',
};

// The one thing an admin actually needs to know at a glance: is this work on
// the public directory? Only an approved, paid, unexpired affiliate is.
const DirectoryChip = ({ listed, testId }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium
                    ${listed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
        data-testid={testId}>
    {listed ? <Globe className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
    {listed ? 'On the public directory' : 'Not on the public directory'}
  </span>
);

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-5 py-4" data-testid={testId}>
    <div className="text-2xl font-semibold text-slate-900">{value}</div>
    <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mt-1">{label}</div>
  </div>
);

const AffiliateCard = ({ a, children, testId }) => (
  <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid={testId}>
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-slate-900">{a.org_name}</h3>
          <Badge className={`${STATUS_STYLE[a.status]} hover:${STATUS_STYLE[a.status]}`}
                 data-testid={`status-badge-${a.id}`}>
            {STATUS_LABEL[a.status] || a.status}
          </Badge>
          <DirectoryChip listed={a.listed} testId={`listed-chip-${a.id}`} />
          <span className="text-xs text-slate-400 uppercase tracking-wider">{a.org_type}</span>
        </div>
        <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <UserRound className="h-3.5 w-3.5" />
            {[a.pastor_title, a.pastor_name].filter(Boolean).join(' ') || '—'}
          </span>
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />{[a.city, a.country].filter(Boolean).join(', ') || '—'}
          </span>
          <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{a.email}</span>
          {a.years_in_ministry != null && <span>{a.years_in_ministry} yrs in ministry</span>}
        </p>
        {a.description && (
          <p className="mt-3 text-sm text-slate-600 line-clamp-3">{a.description}</p>
        )}
        {!!a.documents?.length && (
          <p className="mt-3 flex flex-wrap items-center gap-2">
            {a.documents.map((d) => (
              <a key={d.path} href={d.url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 text-xs text-cyan-700 hover:underline">
                <Paperclip className="h-3 w-3" />{d.name}
              </a>
            ))}
          </p>
        )}
        {a.expires_at && (
          <p className="mt-2 text-xs text-slate-500">
            Membership {a.status === 'lapsed' ? 'expired' : 'runs to'}{' '}
            {new Date(a.expires_at).toLocaleDateString()}
          </p>
        )}
        {a.review_note && (
          <p className="mt-2 text-xs text-slate-500 italic">Note: {a.review_note}</p>
        )}
        {a.approval_email && (
          <p className="mt-2 text-xs text-slate-500" data-testid={`approval-email-${a.id}`}>
            Approval &amp; fee email{' '}
            {a.approval_email.sent ? 'sent to' : a.approval_email.queued
              ? 'queued for' : 'not sent to'}{' '}
            {a.approval_email.to || 'no address'}
            {a.approval_email.invoice_number ? ` · invoice ${a.approval_email.invoice_number}` : ''}
            {!a.approval_email.sent && a.approval_email.reason
              ? ` · ${a.approval_email.reason}` : ''}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">{children}</div>
    </div>
  </div>
);

const TABS = ['applications', 'members', 'lapsed', 'rejected', 'fee', 'comms', 'appearance'];

export default function AdminAffiliatedMinistries() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'applications';
  const setTab = (next) => {
    params.set('tab', next);
    setParams(params, { replace: true });
  };
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  const [messaging, setMessaging] = useState(null);
  const [dm, setDm] = useState({ subject: '', body: '' });
  const [markPaid, setMarkPaid] = useState(null);
  const [payment, setPayment] = useState({ method: 'bank', reference: '' });
  const [fee, setFee] = useState(null);
  const [cast, setCast] = useState({
    kind: 'announcement', subject: '', body: '', audienceType: 'all',
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['affiliate-stats'] });
    qc.invalidateQueries({ queryKey: ['affiliate-list'] });
    qc.invalidateQueries({ queryKey: ['affiliate-comms'] });
  };

  const { data: stats } = useQuery({
    queryKey: ['affiliate-stats'],
    queryFn: () => api.get('/admin/affiliates/stats').then((r) => r.data),
  });
  const { data: list, isLoading } = useQuery({
    queryKey: ['affiliate-list'],
    queryFn: () => api.get('/admin/affiliates').then((r) => r.data),
  });
  const { data: comms } = useQuery({
    queryKey: ['affiliate-comms'],
    queryFn: () => api.get('/admin/affiliates/communications').then((r) => r.data),
  });

  const rows = list?.affiliates || [];
  const byStatus = (s) => rows.filter((r) => r.status === s);
  const settings = fee || stats?.settings;

  const act = useMutation({
    mutationFn: ({ url, body }) => api.post(url, body || {}),
    onSuccess: (res, vars) => {
      toast.success(vars.done || 'Done');
      if (res.data?.portal_url) {
        navigator.clipboard?.writeText(res.data.portal_url).catch(() => {});
        toast.info('Portal link copied to your clipboard');
      }
      setRejecting(null); setNote('');
      setMessaging(null); setDm({ subject: '', body: '' });
      setMarkPaid(null); setPayment({ method: 'bank', reference: '' });
      setCast((c) => ({ ...c, subject: '', body: '' }));
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e, 'That did not work')),
  });

  const openCertificate = async (a) => {
    try {
      const res = await api.get(`/admin/affiliates/${a.id}/certificate.pdf`,
                                { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      invalidate();
    } catch (e) {
      toast.error(getErrorMessage(e, 'The certificate could not be produced'));
    }
  };

  const saveFee = useMutation({
    mutationFn: (payload) => api.put('/admin/affiliates/settings/fee', payload),
    onSuccess: (res) => {
      setFee(res.data.settings);
      toast.success('Fee configuration saved');
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save that')),
  });

  const openInvoiceFor = useQuery({
    queryKey: ['affiliate-detail', markPaid?.id],
    queryFn: () => api.get(`/admin/affiliates/${markPaid.id}`).then((r) => r.data),
    enabled: Boolean(markPaid?.id),
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-affiliates-page">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3">
          <HandHeart className="h-7 w-7 text-amber-600" /> Affiliated Ministries
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Independent churches and ministries under the Archbishop's covering. Kept entirely
          separate from the FWM church network — they never count towards assembly totals.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Pending" value={stats?.pending_count ?? 0} testId="stat-pending" />
        <Stat label="Awaiting payment" value={stats?.awaiting_payment_count ?? 0} testId="stat-awaiting" />
        <Stat label="Approved · listed" value={stats?.active_count ?? 0} testId="stat-active" />
        <Stat label="Lapsed" value={stats?.lapsed_count ?? 0} testId="stat-lapsed" />
        <Stat label="Rejected" value={stats?.rejected_count ?? 0} testId="stat-rejected" />
        <Stat label="Fees collected"
              value={formatCurrency(stats?.fees_collected ?? 0, settings?.currency || 'USD')}
              testId="stat-collected" />
      </div>

      <p className="flex items-start gap-2 text-xs text-slate-500 border border-slate-200
                    bg-slate-50 rounded-lg px-3 py-2.5" data-testid="directory-rule-note">
        <Globe className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          An affiliate appears on the public directory only once the application is approved
          <em className="not-italic font-semibold"> and </em>
          the membership fee is recorded as paid. Pending, rejected, awaiting-payment and lapsed
          records are never shown publicly.
        </span>
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="affiliates-tabs">
          <TabsTrigger value="applications" data-testid="tab-applications">
            Applications{stats?.pending_count ? ` (${stats.pending_count})` : ''}
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          <TabsTrigger value="lapsed" data-testid="tab-lapsed">Lapsed</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">
            Rejected{stats?.rejected_count ? ` (${stats.rejected_count})` : ''}
          </TabsTrigger>
          <TabsTrigger value="fee" data-testid="tab-fee">Fee</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comms">
            Communications{comms?.unread ? ` (${comms.unread})` : ''}
          </TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">Appearance</TabsTrigger>
        </TabsList>

        {/* ── Applications ── */}
        <TabsContent value="applications" className="space-y-3 mt-4">
          {isLoading ? <Skeleton className="h-28 w-full" />
            : !byStatus('pending').length ? (
              <p className="text-sm text-slate-500 py-8" data-testid="applications-empty">
                No applications waiting for review.
              </p>
            ) : byStatus('pending').map((a) => (
              <AffiliateCard key={a.id} a={a} testId={`application-${a.slug}`}>
                <Button size="sm" disabled={act.isPending}
                        onClick={() => act.mutate({
                          url: `/admin/affiliates/${a.id}/approve`,
                          done: `${a.org_name} approved — invoice sent`,
                        })}
                        data-testid={`approve-${a.slug}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => { setRejecting(a); setNote(''); }}
                        data-testid={`reject-${a.slug}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
                </Button>
              </AffiliateCard>
            ))}
        </TabsContent>

        {/* ── Members (awaiting payment + active) ── */}
        <TabsContent value="members" className="space-y-3 mt-4">
          {[...byStatus('awaiting_payment'), ...byStatus('active')].length === 0 ? (
            <p className="text-sm text-slate-500 py-8" data-testid="members-empty">
              No affiliates yet — approve an application to get started.
            </p>
          ) : [...byStatus('awaiting_payment'), ...byStatus('active')].map((a) => (
            <AffiliateCard key={a.id} a={a} testId={`member-${a.slug}`}>
              {a.status === 'awaiting_payment' && (
                <Button size="sm" onClick={() => setMarkPaid(a)} data-testid={`mark-paid-${a.slug}`}>
                  <Wallet className="h-3.5 w-3.5 mr-1.5" /> Record payment
                </Button>
              )}
              {a.status === 'active' && (
                <Button size="sm" variant="outline" disabled={act.isPending}
                        onClick={() => act.mutate({
                          url: `/admin/affiliates/${a.id}/renew`,
                          done: `Renewal invoice sent to ${a.org_name}`,
                        })}
                        data-testid={`renew-${a.slug}`}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Invoice renewal
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setMessaging(a)}
                      data-testid={`message-${a.slug}`}>
                <Send className="h-3.5 w-3.5 mr-1.5" /> Message
              </Button>
              <Button size="sm" variant="ghost" disabled={act.isPending}
                      onClick={() => act.mutate({
                        url: `/admin/affiliates/${a.id}/resend-link`,
                        done: `Fresh portal link emailed to ${a.org_name}`,
                      })}
                      data-testid={`resend-${a.slug}`}>
                <Link2 className="h-3.5 w-3.5 mr-1.5" /> Portal link
              </Button>
              {a.can_certify && (
                <>
                  <Button size="sm" variant="outline"
                          onClick={() => openCertificate(a)}
                          data-testid={`certificate-${a.slug}`}>
                    <Award className="h-3.5 w-3.5 mr-1.5" /> Certificate
                  </Button>
                  <Button size="sm" variant="ghost" disabled={act.isPending}
                          onClick={() => act.mutate({
                            url: `/admin/affiliates/${a.id}/certificate/email`,
                            done: `Certificate emailed to ${a.org_name}`,
                          })}
                          data-testid={`certificate-email-${a.slug}`}>
                    <Send className="h-3.5 w-3.5 mr-1.5" /> Email certificate
                  </Button>
                </>
              )}
            </AffiliateCard>
          ))}
        </TabsContent>

        {/* ── Lapsed ── */}
        <TabsContent value="lapsed" className="space-y-3 mt-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-slate-500">
              Lapsed affiliates are hidden from the public directory until they renew.
            </p>
            <Button size="sm" variant="outline" disabled={act.isPending}
                    onClick={() => act.mutate({
                      url: '/admin/affiliates/run-renewal-sweep',
                      done: 'Renewal sweep complete',
                    })}
                    data-testid="run-sweep-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Run renewal sweep now
            </Button>
          </div>
          {!byStatus('lapsed').length ? (
            <p className="text-sm text-slate-500 py-8" data-testid="lapsed-empty">
              Nobody has lapsed. Good news.
            </p>
          ) : byStatus('lapsed').map((a) => (
            <AffiliateCard key={a.id} a={a} testId={`lapsed-${a.slug}`}>
              <Button size="sm" disabled={act.isPending}
                      onClick={() => act.mutate({
                        url: `/admin/affiliates/${a.id}/renew`,
                        done: `Renewal invoice sent to ${a.org_name}`,
                      })}
                      data-testid={`lapsed-renew-${a.slug}`}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Invoice renewal
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMarkPaid(a)}
                      data-testid={`lapsed-mark-paid-${a.slug}`}>
                <Wallet className="h-3.5 w-3.5 mr-1.5" /> Record payment
              </Button>
            </AffiliateCard>
          ))}
        </TabsContent>

        {/* ── Rejected ── */}
        <TabsContent value="rejected" className="space-y-3 mt-4">
          <p className="text-sm text-slate-500">
            Rejected applications are kept for the record and are never shown publicly.
            Reopening one puts it back in the review queue.
          </p>
          {!byStatus('rejected').length ? (
            <p className="text-sm text-slate-500 py-8" data-testid="rejected-empty">
              Nothing has been rejected.
            </p>
          ) : byStatus('rejected').map((a) => (
            <AffiliateCard key={a.id} a={a} testId={`rejected-${a.slug}`}>
              <Button size="sm" variant="outline" disabled={act.isPending}
                      onClick={() => act.mutate({
                        url: `/admin/affiliates/${a.id}/reopen`,
                        done: `${a.org_name} is back in the review queue`,
                      })}
                      data-testid={`reopen-${a.slug}`}>
                <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Reopen application
              </Button>
            </AffiliateCard>
          ))}
        </TabsContent>

        {/* ── Fee configuration ── */}
        <TabsContent value="fee" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings2 className="h-4 w-4" /> Membership fee
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {!settings ? <Skeleton className="h-40 w-full" /> : (
                <>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div>
                      <Label className="text-xs">Annual fee</Label>
                      <Input type="number" step="0.01" min="0" value={settings.fee_amount}
                             onChange={(e) => setFee({ ...settings, fee_amount: e.target.value })}
                             data-testid="fee-amount" className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs">Currency</Label>
                      <Input value={settings.currency} maxLength={3}
                             onChange={(e) => setFee({ ...settings, currency: e.target.value.toUpperCase() })}
                             data-testid="fee-currency" className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs">Term (months)</Label>
                      <Input type="number" min="1" value={settings.term_months}
                             onChange={(e) => setFee({ ...settings, term_months: e.target.value })}
                             data-testid="fee-term" className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs">Grace days after expiry</Label>
                      <Input type="number" min="0" value={settings.grace_days}
                             onChange={(e) => setFee({ ...settings, grace_days: e.target.value })}
                             data-testid="fee-grace" className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Renewal reminders — days before expiry</Label>
                    <Input value={(settings.reminder_days || []).join(', ')}
                           onChange={(e) => setFee({ ...settings, reminder_days: e.target.value })}
                           data-testid="fee-reminders" className="mt-1.5"
                           placeholder="60, 30, 14, 7, 1" />
                    <p className="text-xs text-slate-500 mt-1.5">
                      Comma separated. A renewal invoice is raised with the first reminder.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Directory intro</Label>
                    <Textarea rows={3} value={settings.intro}
                              onChange={(e) => setFee({ ...settings, intro: e.target.value })}
                              data-testid="fee-intro" className="mt-1.5" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={Boolean(settings.applications_open)}
                            onCheckedChange={(v) => setFee({ ...settings, applications_open: v })}
                            data-testid="fee-applications-open" />
                    <span className="text-sm text-slate-600">
                      Accept new affiliation applications
                    </span>
                  </div>
                  <Button disabled={saveFee.isPending} data-testid="fee-save"
                          onClick={() => saveFee.mutate({
                            fee_amount: Number(settings.fee_amount),
                            currency: settings.currency,
                            term_months: Number(settings.term_months),
                            grace_days: Number(settings.grace_days),
                            applications_open: Boolean(settings.applications_open),
                            intro: settings.intro,
                            reminder_days: String(settings.reminder_days)
                              .split(',').map((d) => Number(String(d).trim()))
                              .filter((d) => !Number.isNaN(d)),
                          })}>
                    {saveFee.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save configuration
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Communications ── */}
        <TabsContent value="comms" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Megaphone className="h-4 w-4" /> Broadcast to affiliates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={cast.kind} onValueChange={(v) => setCast({ ...cast, kind: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="cast-kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="announcement">Announcement</SelectItem>
                      <SelectItem value="newsletter">Newsletter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Audience</Label>
                  <Select value={cast.audienceType}
                          onValueChange={(v) => setCast({ ...cast, audienceType: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="cast-audience">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Active affiliates</SelectItem>
                      <SelectItem value="everyone">Everyone (incl. awaiting &amp; lapsed)</SelectItem>
                      <SelectItem value="awaiting_payment">Awaiting payment only</SelectItem>
                      <SelectItem value="lapsed">Lapsed only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Input value={cast.subject} placeholder="Subject"
                     onChange={(e) => setCast({ ...cast, subject: e.target.value })}
                     data-testid="cast-subject" />
              <Textarea rows={5} value={cast.body} placeholder="Write your message…"
                        onChange={(e) => setCast({ ...cast, body: e.target.value })}
                        data-testid="cast-body" />
              <Button disabled={act.isPending || !cast.subject.trim() || !cast.body.trim()}
                      data-testid="cast-send"
                      onClick={() => act.mutate({
                        url: '/admin/affiliates/communications/broadcast',
                        body: {
                          kind: cast.kind, subject: cast.subject, body: cast.body,
                          audience: ['all', 'everyone'].includes(cast.audienceType)
                            ? { type: cast.audienceType, values: [] }
                            : { type: 'status', values: [cast.audienceType] },
                        },
                        done: 'Broadcast sent',
                      })}>
                {act.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                               : <Send className="h-4 w-4 mr-2" />}
                Send broadcast
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Inbox className="h-4 w-4" /> Message history
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!comms?.messages?.length ? (
                <p className="p-6 text-sm text-slate-500" data-testid="comms-empty">
                  No messages yet.
                </p>
              ) : (
                <div className="divide-y" data-testid="comms-list">
                  {comms.messages.map((m) => (
                    <div key={m.id} className="px-6 py-4" data-testid={`comm-${m.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                          {m.direction === 'inbound' ? 'From affiliate' : m.kind}
                        </Badge>
                        <span className="text-sm font-medium text-slate-900">{m.subject}</span>
                        {m.direction === 'inbound' && !m.read_by_admin && (
                          <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100 text-[10px]">
                            New
                          </Badge>
                        )}
                        <span className="text-xs text-slate-400 ml-auto">
                          {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs text-slate-500">
                        {m.direction === 'inbound'
                          ? m.affiliate_name
                          : `${m.sent_by_name || 'Admin'} → ${m.recipient_count} recipient${m.recipient_count === 1 ? '' : 's'}`}
                      </p>
                      <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{m.body}</p>
                      {m.direction === 'inbound' && !m.read_by_admin && (
                        <Button size="sm" variant="ghost" className="mt-2 h-7 px-2"
                                onClick={() => act.mutate({
                                  url: `/admin/affiliates/communications/${m.id}/read`,
                                  done: 'Marked as read',
                                })}
                                data-testid={`comm-read-${m.id}`}>
                          Mark as read
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Appearance ── */}
        <TabsContent value="appearance" className="mt-4">
          <AffiliateAppearanceTab />
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={Boolean(rejecting)} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {rejecting?.org_name}</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="A short note the applicant will receive"
                    data-testid="reject-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button disabled={act.isPending || !note.trim()} data-testid="reject-confirm"
                    onClick={() => act.mutate({
                      url: `/admin/affiliates/${rejecting.id}/reject`,
                      body: { note },
                      done: `${rejecting.org_name} rejected`,
                    })}>
              Reject application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct message dialog */}
      <Dialog open={Boolean(messaging)} onOpenChange={(v) => !v && setMessaging(null)}>
        <DialogContent data-testid="dm-dialog">
          <DialogHeader>
            <DialogTitle>Message {messaging?.org_name}</DialogTitle>
          </DialogHeader>
          <Input value={dm.subject} placeholder="Subject"
                 onChange={(e) => setDm({ ...dm, subject: e.target.value })}
                 data-testid="dm-subject" />
          <Textarea rows={5} value={dm.body} placeholder="Your message"
                    onChange={(e) => setDm({ ...dm, body: e.target.value })}
                    data-testid="dm-body" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessaging(null)}>Cancel</Button>
            <Button disabled={act.isPending || !dm.subject.trim() || !dm.body.trim()}
                    data-testid="dm-send"
                    onClick={() => act.mutate({
                      url: `/admin/affiliates/${messaging.id}/messages`,
                      body: dm,
                      done: 'Message sent',
                    })}>
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={Boolean(markPaid)} onOpenChange={(v) => !v && setMarkPaid(null)}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Record payment · {markPaid?.org_name}</DialogTitle>
          </DialogHeader>
          {(() => {
            const inv = (openInvoiceFor.data?.invoices || [])
              .find((i) => i.status === 'unpaid');
            if (openInvoiceFor.isLoading) return <Skeleton className="h-20 w-full" />;
            if (!inv) {
              return (
                <p className="text-sm text-slate-500" data-testid="mark-paid-none">
                  There is no outstanding invoice. Raise a renewal invoice first.
                </p>
              );
            }
            return (
              <>
                <p className="text-sm text-slate-600">
                  Invoice {inv.number} · <strong>{formatCurrency(inv.amount, inv.currency)}</strong>
                </p>
                <div>
                  <Label className="text-xs">Method</Label>
                  <Select value={payment.method}
                          onValueChange={(v) => setPayment({ ...payment, method: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="mark-paid-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="mobile_money">Mobile money</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input value={payment.reference} placeholder="Reference (optional)"
                       onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
                       data-testid="mark-paid-reference" />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMarkPaid(null)}>Cancel</Button>
                  <Button disabled={act.isPending} data-testid="mark-paid-confirm"
                          onClick={() => act.mutate({
                            url: `/admin/affiliates/invoices/${inv.id}/mark-paid`,
                            body: payment,
                            done: `${markPaid.org_name} is now active`,
                          })}>
                    Confirm payment
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
